import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg, fileUrl } from "@/lib/api";
import { compressImage, fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const SlotStatus = ({ slots, items }) => (
  <div className="space-y-1 rounded-lg border border-border p-3" data-testid="evidence-slot-status">
    {slots.map((s, i) => {
      const ok = items.some((e) => e.slot === s.key);
      return (
        <div key={s.key} className="flex items-center justify-between gap-2 text-sm" data-testid={`evidence-slot-row-${i}`}>
          <span className="truncate">{s.label}</span>
          <span className={cn("shrink-0 text-xs font-semibold", ok ? "text-emerald-400" : "text-amber-400")}>{ok ? "Con foto" : "Falta foto"}</span>
        </div>
      );
    })}
  </div>
);

export const EvidencePanel = ({ woId, editable, defaultCategory = "durante", onCount, units = [], fixedUnitId = "", serviceId = "", addOnly = false }) => {
  const { meta } = useAuth();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState(defaultCategory);
  const [unitId, setUnitId] = useState(fixedUnitId);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const [caption, setCaption] = useState("");
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [rules, setRules] = useState(null);
  const [slot, setSlot] = useState("");
  const labels = Object.fromEntries((meta?.evidence_categories || []).map((c) => [c.code, c.label]));
  const cats = (rules?.allowed || []).map((code) => ({ code, label: labels[code] || code }));
  const full = !!rules?.max_photos && items.length >= rules.max_photos;
  const slots = rules?.slots || [];
  const slotLabel = (ev) => slots.find((s) => s.key === ev.slot)?.label || (ev.slot ? ev.caption : rules?.section_label ? "Complementaria" : null);

  const load = useCallback(async () => {
    const [{ data }, r] = await Promise.all([api.get(`/work-orders/${woId}/evidence`), api.get(`/work-orders/${woId}/evidence-rules`, { params: serviceId ? { service_id: serviceId } : {} })]);
    const own = data.filter((e) => (serviceId ? e.service_id === serviceId : !e.service_id) && !(e.slot || "").startsWith("vv"));
    const shown = fixedUnitId ? own.filter((e) => e.unit_id === fixedUnitId) : own;
    setItems(shown);
    setRules(r.data);
    onCount?.(shown.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (rules?.allowed && !rules.allowed.includes(cat)) setCat(rules.allowed[0]);
  }, [rules, cat]);
  useEffect(() => {
    if (slots.length && !slot) setSlot((slots.find((s) => !items.some((e) => e.slot === s.key)) || slots[0]).key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const blob = await compressImage(f);
        const fd = new FormData();
        fd.append("file", blob, "foto.jpg");
        fd.append("category", cat);
        fd.append("caption", caption);
        if (unitId) fd.append("unit_id", unitId);
        if (slot) fd.append("slot", slot);
        if (serviceId) fd.append("service_id", serviceId);
        await api.post(`/work-orders/${woId}/evidence`, fd);
      }
      toast.success(files.length > 1 ? `${files.length} fotografías agregadas` : "Fotografía agregada");
      setCaption("");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/work-orders/${woId}/evidence/${id}`);
      setView(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="evidence-panel">
      {rules?.section_label && <div className="font-mono text-xs uppercase tracking-wider text-primary" data-testid="evidence-section-label">{rules.section_label}</div>}
      {slots.length > 0 && <SlotStatus slots={slots} items={items} />}
      {(editable || addOnly) && (
        <>
          {slots.length > 0 && (
            <select className="h-11 w-full rounded-md border border-border bg-transparent px-2 text-sm" value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="evidence-slot-select">
              {slots.map((s) => <option key={s.key} value={s.key}>{s.label} *</option>)}
              <option value="">Complementaria (cableado o instalación general)</option>
            </select>
          )}
          {units.length > 0 && !fixedUnitId && (
            <select className="h-10 w-full rounded-md border border-border bg-transparent px-2 text-sm" value={unitId} onChange={(e) => setUnitId(e.target.value)} data-testid="evidence-unit-select">
              <option value="">Unidad / vehículo: general de la OS</option>
              {units.map((u, i) => <option key={u.id} value={u.id}>{[`Unidad ${i + 1}`, u.vehicle?.plates || u.vehicle?.economic_number, u.plan?.name].filter(Boolean).join(" · ")}</option>)}
            </select>
          )}
          {!rules?.section_label && <div className="flex gap-2 overflow-x-auto pb-1" data-testid="evidence-category-chips">
            {cats.map((c) => (
              <button key={c.code} type="button" onClick={() => setCat(c.code)} data-testid={`evidence-category-${c.code}`}
                className={cn("h-9 shrink-0 rounded-full border px-4 text-sm transition-colors", cat === c.code ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground")}>
                {c.label}{rules?.required?.includes(c.code) ? " *" : ""}
              </button>
            ))}
          </div>}
          {rules?.required?.length > 0 && <p className="text-xs text-muted-foreground" data-testid="evidence-required-list">Obligatorias (*): {rules.required.map((c) => labels[c] || c).join(", ")}</p>}
          {full && <p className="text-xs text-amber-400" data-testid="evidence-max-reached">Este servicio admite una sola fotografía (ubicación del señuelo). Elimina la actual para reemplazarla.</p>}
          <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Descripción de la foto (opcional)" className="h-11 w-full rounded-md border border-input bg-transparent px-3 text-sm" data-testid="evidence-caption-input" />
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" className="h-14 text-base" disabled={busy || full} onClick={() => cameraRef.current?.click()} data-testid="evidence-camera-button">
              {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Camera className="mr-2 h-5 w-5" />} Tomar fotografía
            </Button>
            <Button type="button" variant="outline" className="h-14 text-base" disabled={busy || full} onClick={() => galleryRef.current?.click()} data-testid="evidence-gallery-button">
              <ImagePlus className="mr-2 h-5 w-5" /> Galería
            </Button>
          </div>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => upload([...e.target.files])} data-testid="evidence-camera-input" />
          <input ref={galleryRef} type="file" accept="image/*" multiple={!rules?.max_photos} hidden onChange={(e) => upload([...e.target.files])} data-testid="evidence-file-input" />
        </>
      )}
      {items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border py-8 text-center text-sm text-muted-foreground">Sin fotografías</p>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {items.map((ev) => (
            <button key={ev.id} type="button" onClick={() => setView(ev)} className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-secondary" data-testid={`evidence-thumb-${ev.id}`}>
              <img src={fileUrl(`/files/evidence/${ev.id}`)} alt={labels[ev.category]} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
              <span className="absolute bottom-1 left-1 max-w-[95%] truncate rounded bg-black/70 px-1.5 py-0.5 font-mono text-[10px] uppercase text-white">{slotLabel(ev) || labels[ev.category] || ev.category}</span>
              {ev.complementary && <span className="absolute left-1 top-1 rounded bg-emerald-700/90 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white" data-testid={`evidence-complementary-${ev.id}`}>Complementaria</span>}
            </button>
          ))}
        </div>
      )}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-3xl p-3" data-testid="evidence-view-dialog">
          {view && (
            <div className="space-y-3">
              <img src={fileUrl(`/files/evidence/${view.id}`)} alt="" className="max-h-[70vh] w-full rounded-md object-contain" />
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>{slotLabel(view) || labels[view.category]}{view.caption && view.caption !== slotLabel(view) ? ` · ${view.caption}` : ""} · {view.uploaded_by_name} · {fmtDateTime(view.created_at)}</span>
                {editable && <Button size="sm" variant="destructive" onClick={() => remove(view.id)} data-testid="evidence-delete-button"><Trash2 className="mr-1 h-4 w-4" />Eliminar</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
